/**
 * Tipos compartidos del módulo Mi Registro Legal (autoservicio del cliente).
 * Espejo de los DTOs del backend en mx.npmx.sicen.kernel.registrolegal.api.
 */

export type TipoPersona = "MORAL" | "FISICA";

export type Estatus =
  | "BORRADOR_INTERNO"
  | "EN_REVISION_QA"
  | "OBSERVADO"
  | "APROBADO"
  | "ACTIVO"
  | "RECHAZADO"
  | "SUSPENDIDO"
  | "BAJA"
  | "CANCELADO"
  | "EN_REVISION_DOCUMENTAL"
  | "PREVENCION_ENVIADA"
  | "SUBSANADO"
  | "MODIFICACION_EN_PROCESO"
  | "MODIFICADO"
  | "NO_PRESENTADO";

export interface DomicilioFiscal {
  calle: string;
  numeroExterior: string;
  numeroInterior?: string | null;
  colonia: string;
  municipio: string;
  entidadFederativa: string;
  codigoPostal: string;
  pais?: string | null;
}

export interface MiRegistroLegalResumen {
  id: string;
  folioRegistro: string | null;
  estatus: Estatus;
  pasoActual: number;
  tipoPersona: TipoPersona;
  puedeActuarComoTitular: boolean;
  puedeActuarComoTramitador: boolean;
  razonSocial: string;
  nombreComercial: string | null;
  rfc: string;
  curp: string | null;
  giroEmpresa: string | null;
  correoPrincipal: string;
  telefono: string;
  paginaElectronica: string | null;
  domicilio: DomicilioFiscal | null;
  sectoresAutorizados: string[];
  consentimientoAvisoPrivacidad: boolean;
  tieneFiliales: boolean;
  fechaCreacion: string | null;
  fechaEnvioRevision: string | null;
  solicitanteUsuarioId: string | null;
  solicitanteCorreo: string | null;
  tramitadorId: string | null;
  tramitadorRazonSocial: string | null;
}

export interface BorradorPaso1Request {
  tipoPersona: TipoPersona;
  puedeActuarComoTitular: boolean;
  puedeActuarComoTramitador: boolean;
  razonSocial: string;
  nombreComercial?: string | null;
  rfc: string;
  curp?: string | null;
  giroEmpresa?: string | null;
  correoPrincipal: string;
  telefono: string;
  paginaElectronica?: string | null;
  sectoresAutorizados?: string[] | null;
  tramitadorId?: string | null;
}

export interface BorradorPaso2Request {
  domicilio: DomicilioFiscal;
}

export interface RepresentanteBorrador {
  // Identidad V114: nombre dividido + legacy nombreCompleto como fallback
  nombres?: string | null;
  apellidoPaterno?: string | null;
  apellidoMaterno?: string | null;
  nombreCompleto?: string | null;

  rfc?: string | null;
  curp?: string | null;
  correo?: string | null;
  telefono?: string | null;
  telefonoExtension?: string | null;

  // Domicilio estructurado V114 (SEPOMEX)
  domicilioCalle?: string | null;
  domicilioNumeroExterior?: string | null;
  domicilioNumeroInterior?: string | null;
  domicilioCodigoPostal?: string | null;
  domicilioColonia?: string | null;
  domicilioMunicipio?: string | null;
  domicilioEntidadFederativa?: string | null;
  domicilioPais?: string | null;

  // Legacy — fallback para reps creados antes de V114
  domicilioLibre?: string | null;

  consentimientoNotificacionElectronica: boolean;
  instrumentoPoderDescripcion?: string | null;
  vigenciaInicio?: string | null;
  vigenciaFin?: string | null;
}

/** Patrones de validación frontend (idénticos al backend). */
export const REGEX_RFC = /^[A-ZÑ&]{3,4}\d{6}[A-V1-9][A-Z1-9][0-9A]$/;
export const REGEX_CURP = /^[A-Z][AEIOUX][A-Z]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[HM][A-Z]{5}[A-Z\d]\d$/;
export const REGEX_CP = /^[0-9]{5}$/;

export interface BorradorPaso3Request {
  representantes: RepresentanteBorrador[];
}

export interface BorradorPaso4Request {
  sectoresAutorizados: string[];
  consentimientoAvisoPrivacidad: boolean;
  tieneFiliales: boolean;
}

export interface SepomexEntrada {
  cp: string;
  asentamiento: string;
  tipoAsenta: string | null;
  municipio: string;
  estado: string;
  ciudad: string | null;
  zona: string | null;
}

/** Etiquetas en español para mostrar en UI. */
export const ESTATUS_LABEL: Record<Estatus, string> = {
  BORRADOR_INTERNO: "Borrador en captura",
  EN_REVISION_QA: "En revisión por Calidad",
  OBSERVADO: "Con observaciones por subsanar",
  APROBADO: "Aprobado",
  ACTIVO: "Activo",
  RECHAZADO: "Rechazado",
  SUSPENDIDO: "Suspendido",
  BAJA: "Dado de baja",
  CANCELADO: "Cancelado",
  EN_REVISION_DOCUMENTAL: "En revisión documental",
  PREVENCION_ENVIADA: "Prevención enviada",
  SUBSANADO: "Subsanado",
  MODIFICACION_EN_PROCESO: "Modificación en proceso",
  MODIFICADO: "Modificado",
  NO_PRESENTADO: "No presentado",
};
